import { Quote } from '@client/model/quotesModel';
import { extractInvestmentMaxs, extractInvestmentMins } from '@client/utils/investmentBounds';
import { useMemo } from 'react';

export interface SliderBounds {
  min: number;
  max: number;
  midPoint: number;
  defaultValue: number;
  isCalculatedFromQuotes: boolean;
}

const DEFAULT_BOUNDS: SliderBounds = {
  min: 100000,
  max: 200000,
  midPoint: 150000,
  defaultValue: 100000,
  isCalculatedFromQuotes: false,
};

export const useSliderBounds = (
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
): SliderBounds => {
  return useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return DEFAULT_BOUNDS;
    }

    try {
      // Filter quotes by policy type
      const filteredQuotes = quotes.filter(quote =>
        selectedTab === 'joint'
          ? quote.policyType === 'Joint Life'
          : quote.policyType === 'Single Life',
      );

      if (filteredQuotes.length === 0) {
        return DEFAULT_BOUNDS;
      }

      const investmentMins = extractInvestmentMins(filteredQuotes);
      const investmentMaxs = extractInvestmentMaxs(filteredQuotes);

      if (investmentMins.length === 0 || investmentMaxs.length === 0) {
        return DEFAULT_BOUNDS;
      }

      const mostRestrictiveMinimum = Math.max(...investmentMins);
      const mostRestrictiveMaximum = Math.min(...investmentMaxs);

      // Ensure min is not greater than max (safety check for data integrity)
      const finalSliderMin = Math.min(mostRestrictiveMinimum, mostRestrictiveMaximum);
      const finalSliderMax = Math.max(mostRestrictiveMinimum, mostRestrictiveMaximum);

      if (finalSliderMax <= finalSliderMin) {
        return DEFAULT_BOUNDS;
      }

      const midPoint = Math.floor((finalSliderMin + finalSliderMax) / 2);

      // Use the first quote's premium amount as default, or midpoint as fallback
      const defaultValue =
        filteredQuotes.length > 0 && filteredQuotes[0].premiumAmount > 0
          ? Math.max(finalSliderMin, Math.min(finalSliderMax, filteredQuotes[0].premiumAmount))
          : midPoint;

      return {
        min: finalSliderMin,
        max: finalSliderMax,
        midPoint,
        defaultValue,
        isCalculatedFromQuotes: true,
      };
    } catch (error) {
      console.error('Error calculating slider bounds:', error);
      return DEFAULT_BOUNDS;
    }
  }, [quotes, selectedTab]);
};
